import React from 'react';
import { streamExtractAegisAiSource } from '../../../Services/AegisAiDocumentsService';
import { getAiSourceType } from '../AegisAiUi';

// Transcripts are re-streamed after a cleanup pass: `text` becomes the cleaned version and `rawText` keeps the original.
// Whole-site extractions list their pages, each with the offset in `text` where its section starts.
const IDLE = { status: '', text: '', rawText: '', cleaning: false, pages: [], result: null, error: '' };

// Starts extracting as soon as a source is chosen (file picked / valid link pasted), so the text is
// usually ready by the time the user presses "Extract & Review". A new source aborts the old stream.
export default function useSourceExtraction(token, source) {
    const [state, setState] = React.useState(IDLE);
    const [attempt, setAttempt] = React.useState(0);

    React.useEffect(() => {
        if (!source || !token) {
            setState(IDLE);
            return undefined;
        }
        const controller = new AbortController();
        let buffer = '';
        let rawText = '';
        let cleanedStarted = false;
        let frame = 0;
        // Chunks can arrive faster than the screen refreshes; publish at most once per frame.
        const flush = () => {
            frame = 0;
            setState((current) => ({ ...current, text: buffer }));
        };
        const finish = (patch) => {
            cancelAnimationFrame(frame);
            setState((current) => ({ ...current, text: buffer, ...patch }));
        };

        // A video is uploaded as-is; the API pulls its audio track out with ffmpeg the moment it arrives.
        setState({ ...IDLE, status: getAiSourceType(source.sourceType).kind === 'file' ? 'Uploading file...' : getAiSourceType(source.sourceType).kind === 'text' ? 'Processing custom text...' : 'Connecting...' });
        streamExtractAegisAiSource(token, source, {
            onStatus: (status) => setState((current) => ({ ...current, status })),
            onChunk: (piece) => {
                buffer += piece;
                if (!frame) frame = requestAnimationFrame(flush);
            },
            onPage: ({ url, title }) => {
                const page = { url, title, start: buffer.length };
                setState((current) => ({ ...current, pages: [...current.pages, page] }));
            },
            // The raw text stays on screen until the first cleaned piece arrives to replace it.
            onCleaning: (status) => {
                rawText = buffer;
                setState((current) => ({ ...current, status, rawText, cleaning: true }));
            },
            onCleaned: (piece) => {
                if (!cleanedStarted) {
                    cleanedStarted = true;
                    buffer = '';
                }
                buffer += piece;
                if (!frame) frame = requestAnimationFrame(flush);
            },
            onDone: (result) => finish({ result, cleaning: false }),
            onError: (error) => finish({ error }),
        }, controller.signal);

        return () => {
            controller.abort();
            cancelAnimationFrame(frame);
        };
    }, [token, source, attempt]);

    const retry = React.useCallback(() => setAttempt((count) => count + 1), []);
    return { ...state, retry };
}
